export type RequestErrorBody = {
  error?: string;
  errors?: Array<{
    code?: string;
    path?: string[];
    message?: string;
    error?: string;
  }>;
};

export class RequestError extends Error {
  status: number;
  code?: number;
  /** Parsed JSON response body when available (e.g. contains `errors` array) */
  body?: RequestErrorBody;
  serverMessage?: string;
  url?: string;
  isBackend?: boolean;
  constructor(
    message: string,
    status: number,
    code?: number,
    body?: RequestErrorBody,
    context?: { serverMessage?: string; url?: string; isBackend?: boolean },
  ) {
    super(message);
    this.name = "RequestError";
    this.status = status;
    this.code = code;
    this.body = body;
    this.serverMessage = context?.serverMessage;
    this.url = context?.url;
    this.isBackend = context?.isBackend;
  }
}

export const isRequestError = (e: unknown): e is RequestError =>
  e instanceof RequestError;

export const getServerMessage = (
  body?: {
    error?: string | { message?: string };
    errors?: Array<{ error?: string; message?: string }>;
  } | null,
): string | undefined =>
  [
    typeof body?.error === "string" ? body.error : body?.error?.message,
    ...(Array.isArray(body?.errors) ? body.errors : []).flatMap((entry) => [
      entry?.message,
      entry?.error,
    ]),
  ].find(
    (message): message is string =>
      typeof message === "string" && message.length > 0,
  );

/** Unlocalized Lokalise key rather than prose: a missing translation or a dev-facing error */
const RAW_TRANSLATION_KEY = /^[a-z0-9_.]+$/;

/** Only our own backend classifies 4xx this way; WordPress, search and the CDN do not */
export const isUserFacingRequestError = (e: unknown): e is RequestError =>
  isRequestError(e) &&
  e.isBackend === true &&
  !!e.serverMessage &&
  e.status >= 400 &&
  e.status < 500 &&
  e.status !== 401 &&
  !RAW_TRANSLATION_KEY.test(e.serverMessage);
